import _extends from '@babel/runtime/helpers/extends';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import * as React from 'react';
import useUniqueId from '../../util/useUniqueId.js';
import { Box } from '../Box/Box.js';
import { Button as ButtonMemoized } from '../Button/Button.js';
import { Icon } from '../Icon/Icon.js';
import { TextLink } from '../TextLink/TextLink.js';
import { Typography } from '../Typography/Typography.js';

var _excluded = ["actionType", "actionPosition", "contentSummary", "view", "children", "collapsedActionText", "expandedActionText", "actionAttributes", "level", "borderRadius", "onToggle", "chevronAlignment"],
  _excluded2 = ["onClick"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var buttonAlignment = {
  'start-below': 'flex-start',
  below: 'center',
  'end-below': 'flex-end'
};
var NOT_EXPANDED_LINE_CLAMP = 2;

/** Accordion is used to organize related content in an easier consumable way through collapse (hide) and expand (show) behavior. */
var Accordion = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var actionType = _ref.actionType,
    actionPosition = _ref.actionPosition,
    contentSummary = _ref.contentSummary,
    view = _ref.view,
    children = _ref.children,
    collapsedActionText = _ref.collapsedActionText,
    expandedActionText = _ref.expandedActionText,
    actionAttributes = _ref.actionAttributes,
    level = _ref.level,
    borderRadius = _ref.borderRadius,
    onToggle = _ref.onToggle,
    chevronAlignment = _ref.chevronAlignment,
    rest = _objectWithoutProperties(_ref, _excluded);
  var _React$useState = React.useState(view === 'expanded'),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    isExpanded = _React$useState2[0],
    setExpanded = _React$useState2[1];
  React.useEffect(function () {
    return setExpanded(view === 'expanded');
  }, [view]);
  var arrow = React.useMemo(function () {
    return isExpanded ? 'line.arrow.thin-collapse' : 'line.arrow.thin-dropdown';
  }, [isExpanded]);
  var contentSummaryId = useUniqueId();
  var contentId = useUniqueId();
  var change = React.useCallback(function () {
    onToggle === null || onToggle === void 0 || onToggle(!isExpanded);
    setExpanded(!isExpanded);
  }, [isExpanded, onToggle]);
  var changeAttributes = React.useMemo(function () {
    var _ref2 = actionAttributes || {},
      _onClick = _ref2.onClick,
      attrs = _objectWithoutProperties(_ref2, _excluded2);
    return _objectSpread(_objectSpread({}, attrs), {}, {
      onClick: function onClick(event) {
        _onClick === null || _onClick === void 0 || _onClick(event);
        change();
      }
    });
  }, [actionAttributes, change]);

  // Accordion without expanded content does not make sense
  if (React.Children.count(children) === 0) {
    return null;
  }
  var contentSummaryElement = typeof contentSummary === 'string' ? /*#__PURE__*/React.createElement(Typography, {
    variant: "body-s-strong",
    textAlign: "start",
    lineClamp: !isExpanded ? NOT_EXPANDED_LINE_CLAMP : undefined
  }, contentSummary) : contentSummary;
  var childrenElement = isExpanded ? /*#__PURE__*/React.createElement(Box, _extends({
    display: "flex",
    flexDirection: "column",
    role: "region",
    id: contentId,
    "aria-labelledby": contentSummaryId
  }, actionType === 'chevron-icon' ? {
    p: '16',
    pt: '4' // small gap between hover overlay and content }
  } : {
    mb: '16' // spacing between content and action element
  }), children) : null;

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  var actionLabel = isExpanded ? expandedActionText : collapsedActionText;

  // Constructing the heading tag based on the level prop
  var headingTag = "h".concat(level);
  var flexProps = {
    style: {
      flexDirection: 'column',
      display: 'flex'
    }
  };
  return /*#__PURE__*/React.createElement(Box, _extends({}, rest, {
    ref: ref,
    display: "flex",
    flexDirection: "column",
    borderRadius: borderRadius,
    textColor: actionType === 'chevron-icon' ? 'deprecated.secondary.interactive' : undefined
  }), /*#__PURE__*/React.createElement(Typography, _extends({}, flexProps, {
    tag: headingTag
  }), actionType !== 'chevron-icon' && /*#__PURE__*/React.createElement(Box, {
    display: "flex",
    id: contentSummaryId,
    mb: "12"
  }, contentSummaryElement), actionType === 'chevron-icon' && /*#__PURE__*/React.createElement(Box, _extends({}, changeAttributes, {
    as: "button",
    display: "flex",
    alignItems: chevronAlignment || 'center',
    justifyContent: "space-between",
    p: "16",
    id: contentSummaryId,
    interactive: "product.primary",
    textColor: "deprecated.secondary.interactive",
    borderRadius: borderRadius,
    "aria-expanded": isExpanded,
    "aria-controls": contentId
  }), contentSummaryElement, /*#__PURE__*/React.createElement(Box, {
    display: "flex",
    ms: "16"
  }, /*#__PURE__*/React.createElement(Icon, {
    id: arrow,
    size: "14"
  })))), actionType !== 'chevron-icon' && childrenElement, actionType !== 'chevron-icon' && /*#__PURE__*/React.createElement(Box, {
    display: "flex"
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    ,
    alignSelf: buttonAlignment[actionPosition]
  }, actionType === 'button' && /*#__PURE__*/React.createElement(ButtonMemoized, _extends({}, changeAttributes, {
    label: actionLabel,
    variant: "label",
    hierarchy: "secondary",
    size: "s",
    leadingIcon: arrow,
    "aria-expanded": isExpanded,
    "aria-controls": contentId
  })), actionType === 'textlink' && /*#__PURE__*/React.createElement(TextLink, _extends({}, changeAttributes, {
    label: actionLabel,
    leadingIcon: arrow,
    textVariant: "body-s",
    role: "button",
    "aria-expanded": isExpanded,
    "aria-controls": contentId
  }))), actionType === 'chevron-icon' && childrenElement);
});
Accordion.defaultProps = {
  backgroundColor: 'base.transparent',
  actionPosition: 'start-below',
  view: 'collapsed',
  borderRadius: '8'
};
var AccordionMemoized = /*#__PURE__*/React.memo(Accordion);
AccordionMemoized.displayName = 'Accordion';

export { AccordionMemoized as Accordion };
//# sourceMappingURL=Accordion.js.map
